/* Public read-only sample detail at /s/<sessionId>/<sampleId>. This
   is what someone sees when they scan a small printed label's QR
   code. No auth — the IDs in the URL are the secret. */
import React, { useEffect, useState, useMemo } from "react";
import { DEMO_CUPPINGS } from "./demoData.js";

// Compact radar / spider chart for the public sample view. Same shape
// as CuppingRadar in App.jsx but stripped down — fewer ornaments,
// always shown, sized for a phone screen.
function SampleRadar({ scores, size = 320 }) {
  const axes = [
    { k: "fragrance", l: "Fragrance" },
    { k: "flavor", l: "Flavor" },
    { k: "aftertaste", l: "Aftertaste" },
    { k: "acidity", l: "Acidity" },
    { k: "body", l: "Body" },
    { k: "balance", l: "Balance" },
    { k: "overall", l: "Overall" },
  ];
  const cx = size / 2, cy = size / 2;
  const maxR = size * 0.34;
  const labelR = maxR + size * 0.10;
  const minScore = 6, maxScore = 10;
  const N = axes.length;
  const angleFor = (i) => -Math.PI / 2 + (i * 2 * Math.PI) / N;
  const scoreToR = (s) => Math.max(0, ((Math.max(minScore, Math.min(maxScore, s)) - minScore) / (maxScore - minScore)) * maxR);
  const point = (i, s) => {
    const a = angleFor(i);
    const r = scoreToR(s);
    return [cx + Math.cos(a) * r, cy + Math.sin(a) * r];
  };
  const grid = (level) => {
    const pts = axes.map((_, i) => {
      const a = angleFor(i);
      const r = scoreToR(level);
      return `${cx + Math.cos(a) * r},${cy + Math.sin(a) * r}`;
    });
    return `M${pts.join(" L")} Z`;
  };
  const data = axes.map(({ k }, i) => point(i, Number(scores?.[k]) || 6));
  const dataPath = `M${data.map(([x, y]) => `${x},${y}`).join(" L")} Z`;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width="100%" height={size} style={{ display: "block" }}>
      {[6, 7, 8, 9, 10].map(lvl => (
        <path key={lvl} d={grid(lvl)} fill="none" stroke="#E5E7EB" strokeWidth={lvl === 10 ? 1.2 : 0.7} />
      ))}
      {axes.map((_, i) => {
        const [x, y] = point(i, 10);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="#E5E7EB" strokeWidth={0.7} />;
      })}
      <path d={dataPath} fill="rgba(0,0,0,0.08)" stroke="#000" strokeWidth={1.8} strokeLinejoin="round" />
      {data.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={3} fill="#000" stroke="#fff" strokeWidth={1.5} />
      ))}
      {axes.map(({ k, l }, i) => {
        const a = angleFor(i);
        const lx = cx + Math.cos(a) * labelR;
        const ly = cy + Math.sin(a) * labelR;
        const anchor = Math.abs(Math.cos(a)) < 0.3 ? "middle" : (Math.cos(a) > 0 ? "start" : "end");
        const s = Number(scores?.[k]) || 0;
        return (
          <g key={k}>
            <text x={lx} y={ly} fontSize={9.5} fontWeight={700} fill="#6B7280" fontFamily="Inter, sans-serif" textAnchor={anchor} letterSpacing="0.07em" style={{ textTransform: "uppercase" }}>{l}</text>
            <text x={lx} y={ly + 13} fontSize={12} fontWeight={800} fill="#111" fontFamily="Inter, sans-serif" textAnchor={anchor}>{s ? s.toFixed(2) : "—"}</text>
          </g>
        );
      })}
    </svg>
  );
}

// Mirror of App.sampleShortCode — duplicated rather than imported so
// SampleView's bundle doesn't pull in the entire dashboard.
function sampleShortCode(sessionId, sampleId) {
  const src = `${sessionId || ""}|${sampleId || ""}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < src.length; i++) {
    h ^= src.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(36).toUpperCase().padStart(6, "0").slice(0, 6);
}

const PRIMARY = ["fragrance", "flavor", "aftertaste", "acidity", "body", "balance", "overall"];
const SECONDARY = ["uniformity", "cleanCup", "sweetness"];
const PRETTY = {
  fragrance: "Fragrance / Aroma", flavor: "Flavor", aftertaste: "Aftertaste",
  acidity: "Acidity", body: "Body", balance: "Balance", overall: "Overall",
  uniformity: "Uniformity", cleanCup: "Clean cup", sweetness: "Sweetness",
};

function totalFrom(scores, defects) {
  if (!scores) return 0;
  if (!PRIMARY.some(k => scores[k] != null)) return 0;
  const prim = PRIMARY.reduce((a, k) => a + (Number(scores[k]) || 0), 0);
  const sec = SECONDARY.reduce((a, k) => a + (scores[k] != null ? Number(scores[k]) : 10), 0);
  return +(prim + sec - (Number(defects) || 0)).toFixed(2);
}
function djb2(src) {
  let h = 5381;
  for (let i = 0; i < src.length; i++) h = ((h << 5) + h) + src.charCodeAt(i);
  return Math.abs(h).toString(36).toUpperCase().slice(0, 3).padStart(3, "X");
}
function average(records) {
  const eligible = (records || []).filter(r => r.scores && !r.excludedFromAverage);
  if (eligible.length === 0) return null;
  const out = {};
  for (const k of [...PRIMARY, ...SECONDARY]) {
    const vals = eligible.map(r => r.scores[k] != null ? Number(r.scores[k]) : null).filter(v => v != null);
    if (vals.length > 0) out[k] = +(vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(2);
  }
  const defects = eligible.reduce((s, r) => s + (Number(r.defects) || 0), 0) / eligible.length;
  return { scores: out, defects: +defects.toFixed(2) };
}

export default function SampleView({ sessionId, sampleId, code }) {
  const [state, setState] = useState({ status: "loading", data: null, err: "" });
  const [tab, setTab] = useState("avg");

  useEffect(() => {
    let cancelled = false;
    const shape = (session, idx) => {
      const co = session.coffees[idx];
      return {
        session: { id: session.id, name: session.name || "Cupping session", cuppingDate: session.cuppingDate || null },
        sample: {
          id: co.id, index: idx,
          coffeeName: co.coffeeName || "", sourcedFrom: co.sourcedFrom || "",
          roastDate: co.roastDate || "", expectedNotes: co.expectedNotes || "",
          scores: co.scores || null, defects: co.defects || 0, notes: co.notes || "",
          status: co.status || "",
          cuppings: (co.cuppings || []).map(x => ({
            cupperId: x.cupperId, cupperName: x.cupperName || "",
            scores: x.scores || null, descriptors: x.descriptors || null,
            defects: x.defects || 0, notes: x.notes || "",
            excludedFromAverage: !!x.excludedFromAverage,
          })),
        },
      };
    };
    // Try every demo session/sample for a code match — used to
    // resolve QR labels printed from a demo workspace.
    const demoLookup = () => {
      for (const session of (DEMO_CUPPINGS || [])) {
        const list = session.coffees || [];
        if (code) {
          const idx = list.findIndex(co => sampleShortCode(session.id, co.id) === code);
          if (idx >= 0) return shape(session, idx);
        } else {
          if (session.id !== sessionId) continue;
          const idx = list.findIndex(co => co.id === sampleId);
          if (idx >= 0) return shape(session, idx);
        }
      }
      return null;
    };
    const apiUrl = code
      ? `/api/s/${encodeURIComponent(code)}`
      : `/api/s/${encodeURIComponent(sessionId)}/${encodeURIComponent(sampleId)}`;
    (async () => {
      try {
        const r = await fetch(apiUrl);
        if (r.ok) {
          const data = await r.json();
          if (!cancelled) setState({ status: "ready", data, err: "" });
          return;
        }
        const demo = demoLookup();
        if (demo) {
          if (!cancelled) setState({ status: "ready", data: demo, err: "" });
          return;
        }
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || "Couldn't load");
      } catch (e) {
        const demo = demoLookup();
        if (demo) {
          if (!cancelled) setState({ status: "ready", data: demo, err: "" });
          return;
        }
        if (!cancelled) setState({ status: "error", data: null, err: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, sampleId, code]);

  const view = useMemo(() => {
    if (state.status !== "ready") return null;
    const { sample } = state.data;
    if (tab === "avg") {
      const main = sample.scores && PRIMARY.some(k => sample.scores[k] != null)
        ? { scores: sample.scores, defects: sample.defects }
        : null;
      return main || average(sample.cuppings);
    }
    const r = (sample.cuppings || []).find(x => x.cupperId === tab);
    return r ? { scores: r.scores, defects: r.defects, notes: r.notes } : null;
  }, [state, tab]);

  if (state.status === "loading") return <Shell><div style={{ padding: 40, textAlign: "center", color: "#6B7280" }}>Loading…</div></Shell>;
  if (state.status === "error") return (
    <Shell>
      <div style={{ padding: 40, textAlign: "center" }}>
        <div style={{ fontSize: 28, marginBottom: 10 }}>🔍</div>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: "#111", margin: "0 0 8px" }}>Sample not found</h1>
        <p style={{ fontSize: 14, color: "#6B7280", margin: 0, lineHeight: 1.55 }}>{state.err}</p>
      </div>
    </Shell>
  );

  const { session, sample } = state.data;
  const sampleAutoId = `${djb2(session.id || session.name || "session")}-${String((sample.index || 0) + 1).padStart(2, "0")}`;
  const scored = view && view.scores && PRIMARY.some(k => view.scores[k] != null);
  const total = scored ? totalFrom(view.scores, view.defects) : 0;
  const tColor = total >= 90 ? "#B8860B" : total >= 85 ? "#16A34A" : total >= 80 ? "#111" : "#6B7280";
  const tGrade = total >= 90 ? "Outstanding" : total >= 85 ? "Excellent" : total >= 80 ? "Very Good" : total > 0 ? "Below Specialty" : "Not scored";
  const fmtDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

  return (
    <Shell>
      <div style={{ padding: "20px 16px 24px", borderBottom: "1px solid #F3F4F6", background: "#fff" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <span style={{
            background: "#000", color: "#fff", borderRadius: 999,
            padding: "8px 16px", fontSize: 16, fontWeight: 800,
            letterSpacing: "0.04em", fontVariantNumeric: "tabular-nums",
          }}>{sampleAutoId}</span>
          <ShareButton title={`${sample.coffeeName || "Cupping sample"} — ${sampleAutoId}`} />
          <div style={{ marginLeft: "auto", textAlign: "right" }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: tColor, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
              {total > 0 ? total.toFixed(2) : "—"}
            </div>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6, marginTop: 2 }}>{tGrade}</div>
          </div>
        </div>
        <div style={{ fontSize: 19, fontWeight: 700, color: "#111", lineHeight: 1.25 }}>
          {sample.coffeeName || "Unnamed sample"}
        </div>
        <div style={{ fontSize: 13, color: "#6B7280", marginTop: 4, lineHeight: 1.5 }}>
          Session: {session.name}
          <br />Source: {sample.sourcedFrom || "—"} · Roasted {fmtDate(sample.roastDate)}
        </div>
        {sample.expectedNotes && (
          <div style={{ marginTop: 10, padding: "10px 12px", background: "#FAFAFA", borderLeft: "3px solid #2C5766", borderRadius: "0 8px 8px 0", fontSize: 13, color: "#374151", lineHeight: 1.5 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>Expected notes</div>
            {sample.expectedNotes}
          </div>
        )}
      </div>
      <div style={{ padding: 16 }}>
        {/* Tabs */}
        {(sample.cuppings && sample.cuppings.length > 0) && (
          <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
            <TabBtn active={tab === "avg"} onClick={() => setTab("avg")}>Average</TabBtn>
            {sample.cuppings.map(c => (
              <TabBtn key={c.cupperId} active={tab === c.cupperId} onClick={() => setTab(c.cupperId)} muted={c.excludedFromAverage}>
                {c.cupperName || "Cupper"}
                {c.excludedFromAverage && <span style={{ marginLeft: 6, fontSize: 9, padding: "1px 5px", borderRadius: 4, background: "#FEF3C7", color: "#92400E", letterSpacing: 0.3 }}>EXCLUDED</span>}
              </TabBtn>
            ))}
          </div>
        )}
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, padding: 18 }}>
          {scored ? (
            <>
              <div style={{ marginBottom: 14, padding: "8px 0 4px" }}>
                <SampleRadar scores={view.scores} size={320} />
              </div>
              {PRIMARY.map(k => <Row key={k} label={PRETTY[k]} value={view.scores[k]} />)}
              <div style={{ borderTop: "1px solid #F3F4F6", marginTop: 10, paddingTop: 10 }}>
                {SECONDARY.map(k => <Row key={k} label={PRETTY[k]} value={view.scores[k] ?? 10} muted />)}
                {view.defects > 0 && <Row label="Defect points" value={-Math.abs(view.defects)} negative />}
              </div>
              {view.notes && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #F3F4F6", fontSize: 13, color: "#374151", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                  {view.notes}
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: "center", padding: "20px 0", fontSize: 14, color: "#9CA3AF", fontStyle: "italic" }}>
              {tab === "avg" ? "Not scored yet" : "This cupper hasn't scored this sample yet"}
            </div>
          )}
        </div>
        <div style={{ marginTop: 22, paddingTop: 16, borderTop: "1px solid #F3F4F6", textAlign: "center", fontSize: 12, color: "#9CA3AF", lineHeight: 1.6 }}>
          Cupping sheet created with{" "}
          <a href="https://roaster.elionlabs.org" style={{ color: "#374151", textDecoration: "none", fontWeight: 600 }}>
            Elion Roaster
          </a>
        </div>
      </div>
    </Shell>
  );
}

function ShareButton({ title }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const onClick = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, url }); return; }
      catch { /* user cancelled — fall through to copy */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };
  return (
    <button
      onClick={onClick}
      title="Share this sample"
      aria-label="Share"
      style={{
        width: 36, height: 36, padding: 0, borderRadius: 999,
        border: "1px solid #E5E7EB", background: "#fff", color: "#374151",
        cursor: "pointer", fontFamily: "Inter, sans-serif",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        position: "relative",
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="18" cy="5" r="3" />
        <circle cx="6" cy="12" r="3" />
        <circle cx="18" cy="19" r="3" />
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
      </svg>
      {copied && (
        <span style={{
          position: "absolute", top: "calc(100% + 6px)", left: "50%",
          transform: "translateX(-50%)", whiteSpace: "nowrap",
          background: "#111", color: "#fff", fontSize: 11, fontWeight: 600,
          padding: "4px 8px", borderRadius: 6,
        }}>Link copied</span>
      )}
    </button>
  );
}

function TabBtn({ active, onClick, children, muted }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "7px 13px", borderRadius: 8,
        border: `1px solid ${active ? "#000" : "#E5E7EB"}`,
        background: active ? "#000" : "#fff",
        color: active ? "#fff" : (muted ? "#9CA3AF" : "#374151"),
        fontSize: 13, fontWeight: 600, cursor: "pointer",
        fontFamily: "Inter, sans-serif",
        display: "inline-flex", alignItems: "center",
      }}
    >{children}</button>
  );
}
function Row({ label, value, muted, negative }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0" }}>
      <span style={{ fontSize: 14, color: muted ? "#6B7280" : "#111", fontWeight: muted ? 500 : 600 }}>{label}</span>
      <span style={{
        fontSize: 16, fontWeight: 700, fontVariantNumeric: "tabular-nums",
        color: negative ? "#B91C1C" : (value == null ? "#D1D5DB" : "#111"),
      }}>
        {value == null ? "—" : (negative ? `−${Math.abs(value)}` : Number(value).toFixed(2))}
      </span>
    </div>
  );
}
function Shell({ children }) {
  return (
    <div style={{
      minHeight: "100vh", background: "#F9FAFB",
      fontFamily: "Inter, -apple-system, sans-serif", color: "#111",
      maxWidth: 560, margin: "0 auto",
    }}>{children}</div>
  );
}
